import classNames from 'classnames';
import { SwiperSlide, Swiper, SwiperRef } from 'swiper/react';
import {
  useState, useRef, useCallback, useMemo,
  useEffect,
} from 'react';
import { ReactComponent as SvgArrowWhiteBg } from 'public/assets/icons/arrow-white-bg.svg';
import SwiperCore, { Navigation } from 'swiper';
import { useOffers } from 'api/resort/useOffers';
import { ITALodgingReq } from 'components/common/PlanYourTrip/types';
import { SortByAccommodations, SortByAccommodationsDefault } from 'components/common/SortAccomodations/constants';
import { DateTime } from 'luxon';
import { accommodationSortingBySortType, sortAccommodationsByPrice } from 'util/accommodationsSortings';
import { IAccommodationPage } from 'api/tripadvisor/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { RESORT_IDS_TO_HIDE_LODGING_FOR } from 'ots-constants';
import { hasWeSkiIntegration } from 'components/common/PlanYourTrip/WeSki/resolver';
import { getLocale } from 'i18n/locale';
import { ELocale } from 'i18n/localeEnum';
import { SectionHeader } from '../SectionHeader';
import styles from './styles.module.scss';
import { Accommodation } from '../Accomodation';

SwiperCore.use([Navigation]);

const swiperConfig = {
  320: {
    slidesPerView: 1,
  },
  480: {
    slidesPerView: 1.5,
  },
  1100: {
    slidesPerView: 3,
  },
};

interface Props {
  lodgingProfile?: { dangerouslySetInnerHTML: { __html: string; }; };
  accommodations: IAccommodationPage | null;
  isMappedToWeSki: boolean;
  resortId: string;
}

export const WhereToStaySection = ({
  lodgingProfile, accommodations, isMappedToWeSki, resortId,
}: Props) => {
  const { t } = useOTSTranslation('common');

  const swiperRef = useRef<SwiperRef | null>(null);
  const [swiperSectionHeight, setSwiperSectionHeight] = useState(0);
  const [sliderActiveIndex, setSliderActiveIndex] = useState(0);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const whereToGoSliderInstance = useRef<SwiperCore | null>(null);

  const handlePrevWTG = useCallback(() => {
    if (!whereToGoSliderInstance.current) return;
    whereToGoSliderInstance.current.slidePrev();
  }, []);

  const handleNextWTG = useCallback(() => {
    if (!whereToGoSliderInstance.current) return;
    whereToGoSliderInstance.current.slideNext();
  }, []);

  const now = (DateTime.now().startOf('day'));
  const initialFromDate = now.plus({ days: 14 });
  const initialToDate = initialFromDate.plus({ days: 3 });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formData, setFormData] = useState<ITALodgingReq>({
    checkIn: DateTime.fromJSDate(initialFromDate.toJSDate()).toFormat('yyyy-MM-dd'),
    checkOut: DateTime.fromJSDate(initialToDate.toJSDate()).toFormat('yyyy-MM-dd'),
    numRooms: 1,
    numAdults: 2,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sortBy, setSortBy] = useState<SortByAccommodations>(SortByAccommodationsDefault);

  const locationList = useMemo(
    () => (accommodations?.data ?? []).map((a) => a.location_id).sort(),
    [accommodations],
  );
  const { offerMap, areOffersLoading } = useOffers(
    locationList,
    formData.checkIn,
    formData.checkOut,
    formData.numAdults,
    formData.numAdults,
  );

  const sorted = useMemo(() => {
    if (accommodationSortingBySortType[sortBy]) {
      return sortAccommodationsByPrice(accommodations?.data ?? [], offerMap, sortBy);
    }
    return (accommodations?.data ?? []);
  }, [(accommodations?.data ?? []), sortBy, offerMap]);

  const shouldHideLodging = (isMappedToWeSki) || RESORT_IDS_TO_HIDE_LODGING_FOR.includes(resortId);

  const sectionClass = swiperSectionHeight === 0 && !shouldHideLodging ? styles.zeroMargin : undefined;

  useEffect(() => {
    if (!swiperRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      const height = swiperRef.current?.swiper?.el?.clientHeight ?? 0;
      if (height !== 0) {
        setSwiperSectionHeight(height);
      }
    });
    resizeObserver.observe(swiperRef.current.swiper.el);
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // https://mountainnews.jira.com/browse/OWR-1948
  if (getLocale() === ELocale.IT_IT && hasWeSkiIntegration({ resortId })) {
    return null;
  }

  return (
    <section id={styles.whereToStay} className={classNames([sectionClass, shouldHideLodging && styles.shouldHideLodging])}>
      <SectionHeader
        title={t('resort.overview.whereToStay')}
        description={lodgingProfile}
      />
      {!shouldHideLodging && (
        <div className={styles.whereToStaySwiper}>
          {sliderActiveIndex !== 0 && (
            <button
              type="button"
              ref={prevRef}
              className={classNames(styles.swiperNavButton, styles.swiperButtonPrev)}
              onClick={() => handlePrevWTG()}
            >
              <SvgArrowWhiteBg />
            </button>
          )}

          {sliderActiveIndex !== sorted.length - 1 && (
            <button
              type="button"
              ref={nextRef}
              className={classNames(styles.swiperNavButton, styles.swiperButtonNext)}
              onClick={() => handleNextWTG()}
            >
              <SvgArrowWhiteBg />
            </button>
          )}
          <Swiper
            onSlideChangeTransitionEnd={(swiper) => {
              setSliderActiveIndex(swiper.activeIndex);
            }}
            onSwiper={(swiperInstance) => {
              whereToGoSliderInstance.current = swiperInstance;
            }}
            breakpoints={swiperConfig}
            ref={swiperRef}
          >
            {sorted.map((acc) => (
              <SwiperSlide
                className={styles.height_auto}
                key={acc.location_id}
              >
                <Accommodation
                  className={styles.accommodation}
                  accommodation={acc}
                  isFresh
                  offers={offerMap[acc.location_id]}
                  areOffersLoading={areOffersLoading}
                  withViewDealSection={false}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </section>
  );
};
